// Central API configuration with authentication
// Uses REACT_APP_BACKEND_URL env variable in production, localhost in development

import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:7071';
export const API_URL = `${API_BASE_URL}/api`;

// Session storage keys
const SESSION_TOKEN_KEY = 'session_token';
const SESSION_EXPIRES_KEY = 'session_expires_at';

/**
 * Get the current session token from storage
 */
export const getSessionToken = (): string | null => {
  return sessionStorage.getItem(SESSION_TOKEN_KEY);
};

/**
 * Store the session token after login
 */
export const setSessionToken = (token: string, expiresIn: number): void => {
  sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  // Store expiration time for client-side expiry checks
  const expiresAt = Date.now() + (expiresIn * 1000);
  sessionStorage.setItem(SESSION_EXPIRES_KEY, expiresAt.toString());
};

/**
 * Clear the session token (logout)
 */
export const clearSessionToken = (): void => {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_EXPIRES_KEY);
};

/**
 * Check if the session is expired (client-side check)
 */
export const isSessionExpired = (): boolean => {
  const expiresAt = sessionStorage.getItem(SESSION_EXPIRES_KEY);
  if (!expiresAt) return true;
  return Date.now() > parseInt(expiresAt, 10);
};

/**
 * Configured axios instance with authentication interceptor
 *
 * Usage:
 *   import { api } from '../config/api';
 *   const response = await api.get('/containers');
 *   const response = await api.post('/containers', data);
 */
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add Authorization header to all requests
api.interceptors.request.use(
  (config) => {
    const token = getSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors (session expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired or invalid - clear session and redirect to login
      clearSessionToken();
      sessionStorage.removeItem('user');

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Fetch wrapper that adds Authorization header
 * Use this for gradual migration from fetch() to authenticated requests
 *
 * Usage:
 *   import { apiFetch } from '../config/api';
 *   const response = await apiFetch(`${API_URL}/containers`);
 *   const response = await apiFetch(`${API_URL}/containers`, { method: 'POST', body: JSON.stringify(data) });
 */
export const apiFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getSessionToken();

  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 - session expired
  if (response.status === 401) {
    clearSessionToken();
    sessionStorage.removeItem('user');

    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }

  return response;
};

/**
 * Call backend logout endpoint and clear session
 */
export const logout = async (): Promise<void> => {
  try {
    const token = getSessionToken();
    if (token) {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    }
  } catch (error) {
    console.error('Logout API call failed:', error);
  } finally {
    clearSessionToken();
  }
};

export default API_BASE_URL;
